/**
 * Web counterpart of apps/mobile `BiometricAuthService.canCheckBiometrics`: true when the device
 * offers a user-verifying platform authenticator (Face ID, Touch ID, Windows Hello, fingerprint).
 */
export async function canUseBiometrics(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

const randomChallenge = () => crypto.getRandomValues(new Uint8Array(32))

/**
 * Creates a platform WebAuthn credential that requires user verification (Face ID / fingerprint /
 * PIN of the device) and returns its `rawId`.
 *
 * This is ONLY a local gate: the backend never sees the credential. Server-side trust comes from the
 * ECDSA device key in `biometricDevice.ts`; the credential just makes the browser prompt the user
 * (the web equivalent of mobile `BiometricAuthService.authenticate`). Rejects with the browser's
 * `NotAllowedError` when the user cancels.
 */
export async function enrollUserVerification(deviceId: string, userName: string): Promise<ArrayBuffer> {
  const credential = (await navigator.credentials.create({
    publicKey: {
      rp: { id: location.hostname, name: 'Lolax Models' },
      user: { id: new TextEncoder().encode(deviceId), name: userName, displayName: userName },
      challenge: randomChallenge(),
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'discouraged',
      },
    },
  })) as PublicKeyCredential | null
  if (!credential) throw new Error('No se pudo crear la credencial biométrica')
  return credential.rawId
}

/**
 * Prompts the user to verify with the credential created by `enrollUserVerification`. Resolves
 * `true` on an assertion and `false` when the user cancels or the prompt is aborted.
 *
 * Local gate only: the assertion is never sent or checked anywhere.
 */
export async function verifyUser(credentialId: ArrayBuffer): Promise<boolean> {
  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: randomChallenge(),
        allowCredentials: [{ type: 'public-key', id: credentialId }],
        userVerification: 'required',
        rpId: location.hostname,
      },
    })
    return assertion !== null
  } catch (error) {
    if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError')) {
      return false
    }
    throw error
  }
}
